import { EINVOICE_SALE_TYPE } from '@/utils/einvoice.enum'
import get from 'lodash/get'
import { isPreIssuedStatus } from '@/view/pages/einvoice/utils/lifecycleState.utils'
import { PDF_STATE, resolveIssuedPdfReadiness } from './pdfReadiness.utils'

export const FIELD_DISPLAY_STATE = Object.freeze({
  VALUE: 'value',
  OPTIONAL: 'optional',
  PENDING: 'pending',
  MISSING: 'missing',
  ERROR: 'error',
})

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

function createDisplayMeta({
  value,
  whenMissing = FIELD_DISPLAY_STATE.OPTIONAL,
  pendingLabel = '--',
  optionalLabel = ' ',
  missingLabel = 'Thiếu dữ liệu',
  errorLabel = 'Không lấy được dữ liệu',
}) {
  if (hasMeaningfulValue(value)) {
    return {
      state: FIELD_DISPLAY_STATE.VALUE,
      text: value,
    }
  }

  if (whenMissing === FIELD_DISPLAY_STATE.PENDING) {
    return {
      state: FIELD_DISPLAY_STATE.PENDING,
      text: pendingLabel,
    }
  }

  if (whenMissing === FIELD_DISPLAY_STATE.MISSING) {
    return {
      state: FIELD_DISPLAY_STATE.MISSING,
      text: missingLabel,
    }
  }

  if (whenMissing === FIELD_DISPLAY_STATE.ERROR) {
    return {
      state: FIELD_DISPLAY_STATE.ERROR,
      text: errorLabel,
    }
  }

  return {
    state: FIELD_DISPLAY_STATE.OPTIONAL,
    text: optionalLabel,
  }
}

function formatBlockingError(detail, message) {
  return {
    code: `${detail?.salesInvoiceId || 'invoice'}:${message}`,
    message,
  }
}

function normalizeBackendReadiness(detail = {}) {
  const readiness = detail.readiness || {}
  return {
    isReady: typeof readiness.isReady === 'boolean' ? readiness.isReady : true,
    blockingErrors: Array.isArray(readiness.blockingErrors) ? readiness.blockingErrors : [],
    pendingGeneration: [],
    warnings: Array.isArray(readiness.warnings)
      ? readiness.warnings.map((item) => item.message || item).filter(Boolean)
      : [],
    missingRequiredFields: Array.isArray(readiness.missingRequiredFields)
      ? readiness.missingRequiredFields
      : [],
  }
}

function semanticStateToDisplayState(semantic) {
  const normalized = typeof semantic === 'string' ? semantic.trim().toLowerCase() : ''
  if (normalized === 'pending_generation') return FIELD_DISPLAY_STATE.PENDING
  if (normalized === 'missing_required') return FIELD_DISPLAY_STATE.MISSING
  if (normalized === 'unavailable_due_to_error') return FIELD_DISPLAY_STATE.ERROR
  if (normalized === 'not_required') return FIELD_DISPLAY_STATE.OPTIONAL
  return null
}

function createSemanticDisplayMeta(detail, field, fallbackOptions = {}) {
  const semantic = semanticStateToDisplayState(detail?.fieldSemantics?.[field])
  if (!semantic) {
    return createDisplayMeta(fallbackOptions)
  }

  const hasValue =
    semantic === FIELD_DISPLAY_STATE.VALUE || hasMeaningfulValue(fallbackOptions.value)
  if (hasValue && hasMeaningfulValue(fallbackOptions.value)) {
    return createDisplayMeta(fallbackOptions)
  }

  return createDisplayMeta({
    ...fallbackOptions,
    whenMissing: semantic,
  })
}

export function buildDetailReadiness(detail = {}) {
  const readiness = normalizeBackendReadiness(detail)

  if (detail?.fieldSemantics?.lineItems === 'unavailable_due_to_error') {
    readiness.blockingErrors = readiness.blockingErrors.concat([
      formatBlockingError(detail, 'Không lấy được dòng hàng từ snapshot draft hiện tại'),
    ])
    readiness.isReady = false
  }

  if (!hasMeaningfulValue(detail.invoiceNo)) {
    readiness.pendingGeneration.push('Số hóa đơn sẽ sinh sau khi phát hành')
  }
  if (!hasMeaningfulValue(detail.pattern)) {
    readiness.pendingGeneration.push('Ký hiệu sẽ sinh sau khi phát hành')
  }
  if (!hasMeaningfulValue(detail.serial)) {
    readiness.pendingGeneration.push('Serial sẽ sinh sau khi phát hành')
  }
  if (!hasMeaningfulValue(detail.key)) {
    readiness.pendingGeneration.push('Key FAST sẽ sinh sau phản hồi provider')
  }
  if (!hasMeaningfulValue(detail.keySearch)) {
    readiness.pendingGeneration.push('KeySearch sẽ sinh sau phản hồi provider')
  }

  readiness.pendingGeneration = Array.from(new Set(readiness.pendingGeneration))
  return readiness
}

export function buildDetailDisplay(detail = {}) {
  const saleType = detail.saleType
  const preIssued = isPreIssuedStatus(detail.status)
  const retailOptional = saleType === EINVOICE_SALE_TYPE.RETAIL
  const providerMeta = get(detail, 'einvoice.providerMeta') || {}

  return {
    billNumber: createDisplayMeta({
      value: detail.billNumber,
      whenMissing: FIELD_DISPLAY_STATE.OPTIONAL,
      optionalLabel: 'Chưa có số bill',
    }),
    invoiceNo: createSemanticDisplayMeta(detail, 'invoiceNo', {
      value: detail.invoiceNo,
      whenMissing: preIssued ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.MISSING,
      pendingLabel: '--',
      missingLabel: 'Thiếu dữ liệu sau phát hành',
    }),
    pattern: createSemanticDisplayMeta(detail, 'pattern', {
      value: detail.pattern,
      whenMissing: preIssued ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.MISSING,
      pendingLabel: '--',
      missingLabel: 'Thiếu dữ liệu sau phát hành',
    }),
    serial: createSemanticDisplayMeta(detail, 'serial', {
      value: detail.serial,
      whenMissing: preIssued ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.MISSING,
      pendingLabel: '--',
      missingLabel: 'Thiếu dữ liệu sau phát hành',
    }),
    voucherBook: createSemanticDisplayMeta(detail, 'voucherBook', {
      value: detail.voucherBook,
      whenMissing: FIELD_DISPLAY_STATE.OPTIONAL,
      optionalLabel: 'Chưa cấu hình',
    }),
    paymentMethod: createSemanticDisplayMeta(detail, 'paymentMethod', {
      value: detail.paymentMethod,
      whenMissing: FIELD_DISPLAY_STATE.MISSING,
      missingLabel: 'Thiếu phương thức thanh toán',
    }),
    operationType: createSemanticDisplayMeta(detail, 'operationType', {
      value: detail.operationType,
      whenMissing: FIELD_DISPLAY_STATE.MISSING,
      missingLabel: 'Thiếu loại nghiệp vụ',
    }),
    buyerTaxCode: createSemanticDisplayMeta(detail, 'buyerTaxCode', {
      value: detail.buyerTaxCode,
      whenMissing: retailOptional ? FIELD_DISPLAY_STATE.OPTIONAL : FIELD_DISPLAY_STATE.MISSING,
      optionalLabel: ' ',
      missingLabel: 'Thiếu bắt buộc',
    }),
    buyerAddress: createSemanticDisplayMeta(detail, 'buyerAddress', {
      value: detail.buyerAddress,
      whenMissing: retailOptional ? FIELD_DISPLAY_STATE.OPTIONAL : FIELD_DISPLAY_STATE.MISSING,
      optionalLabel: ' ',
      missingLabel: 'Thiếu bắt buộc',
    }),
    buyerEmail: createSemanticDisplayMeta(detail, 'buyerEmail', {
      value: detail.buyerEmail,
      whenMissing: FIELD_DISPLAY_STATE.OPTIONAL,
      optionalLabel: ' ',
    }),
    buyerPhone: createSemanticDisplayMeta(detail, 'buyerPhone', {
      value: detail.buyerPhone,
      whenMissing: FIELD_DISPLAY_STATE.OPTIONAL,
      optionalLabel: ' ',
    }),
    buyerIdCard: createSemanticDisplayMeta(detail, 'buyerIdCard', {
      value: detail.buyerIdCard,
      whenMissing: FIELD_DISPLAY_STATE.OPTIONAL,
      optionalLabel: ' ',
    }),
    amountInWords: createSemanticDisplayMeta(detail, 'amountInWords', {
      value: detail.amountInWords,
      whenMissing:
        Number(detail.totalAmount) > 0 ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.OPTIONAL,
      pendingLabel: '--',
      optionalLabel: 'Chưa có dữ liệu',
    }),
    key: createSemanticDisplayMeta(detail, 'key', {
      value: detail.key,
      whenMissing: preIssued ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.PENDING,
      pendingLabel: 'Sẽ sinh sau phản hồi provider',
    }),
    keySearch: createSemanticDisplayMeta(detail, 'keySearch', {
      value: providerMeta.keySearch,
      whenMissing: preIssued ? FIELD_DISPLAY_STATE.PENDING : FIELD_DISPLAY_STATE.PENDING,
      pendingLabel: 'Sẽ sinh sau phản hồi provider',
    }),
    branchName: createSemanticDisplayMeta(detail, 'branchName', {
      value: detail.branchName,
      whenMissing: FIELD_DISPLAY_STATE.MISSING,
      missingLabel: 'Thiếu mapping chi nhánh',
    }),
  }
}

export function getDisplayClass(meta = {}) {
  if (meta.state === FIELD_DISPLAY_STATE.MISSING) {
    return 'einvoice-field--missing'
  }
  if (meta.state === FIELD_DISPLAY_STATE.ERROR) {
    return 'einvoice-field--error'
  }
  if (meta.state === FIELD_DISPLAY_STATE.PENDING) {
    return 'einvoice-field--pending'
  }
  if (meta.state === FIELD_DISPLAY_STATE.OPTIONAL) {
    return 'einvoice-field--optional'
  }
  return ''
}

export function buildConfigBlockerMeta(detail = {}) {
  const blockingErrors = Array.isArray(detail?.readiness?.blockingErrors)
    ? detail.readiness.blockingErrors
    : []
  const matching = blockingErrors.find(
    (item) => /unitcode/i.test(item?.code || '') || /unitcode/i.test(item?.message || ''),
  )

  if (matching) {
    return {
      isBlocked: true,
      title: 'Blocked bởi cấu hình FAST',
      message: matching.message,
      howToFix: 'Vào Cài đặt HĐĐT và cấu hình unitCode theo chi nhánh phát hành.',
    }
  }

  if (/unitcode is not configured/i.test(detail?.feedbackContent || '')) {
    return {
      isBlocked: true,
      title: 'Blocked bởi cấu hình FAST',
      message: detail.feedbackContent,
      howToFix: 'Vào Cài đặt HĐĐT và cấu hình unitCode theo chi nhánh phát hành.',
    }
  }

  return {
    isBlocked: false,
    title: '',
    message: '',
    howToFix: '',
  }
}

export function buildArtifactDisplay(detail = {}) {
  const artifacts = detail?.artifacts || {}
  const previewPdf = artifacts.previewPdf || null
  const issuedPdf = artifacts.issuedPdf || null
  const issuedPdfReadiness = resolveIssuedPdfReadiness(issuedPdf)
  const xml = artifacts.xml || null

  const issuedPdfLabelByState = {
    [PDF_STATE.READY]: 'Sẵn sàng',
    [PDF_STATE.COOLDOWN]: 'Đang cooldown',
    [PDF_STATE.NOT_READY]: 'Chưa sẵn sàng',
    [PDF_STATE.FAILED]: 'Tạo PDF thất bại',
    [PDF_STATE.UNKNOWN]: 'Không xác định',
  }

  return {
    previewPdf: {
      available: Boolean(previewPdf?.available),
      label: previewPdf?.available ? 'Sẵn sàng' : 'Chưa sẵn sàng',
      route: previewPdf?.route || null,
    },
    issuedPdf: {
      available: Boolean(issuedPdf?.available),
      label: issuedPdfLabelByState[issuedPdfReadiness.state] || 'Không xác định',
      route: issuedPdf?.route || null,
      state: issuedPdfReadiness.state,
      retryAfterSeconds: issuedPdfReadiness.retryAfterSeconds,
      lastCheckedAt: issuedPdfReadiness.lastCheckedAt,
      remainingCooldownSeconds: issuedPdfReadiness.remainingSeconds,
      isCooldownActive: issuedPdfReadiness.isCooldownActive,
      reason: issuedPdfReadiness.reason,
    },
    xml: {
      available: Boolean(xml?.available),
      label: xml?.available ? 'Sẵn sàng' : 'Chưa sẵn sàng',
      route: xml?.route || null,
    },
    normalizedPayloadHash: detail?.normalizedPayloadHash || null,
    lookupKey: issuedPdf?.lookupKey || null,
    lookupUrl: issuedPdf?.lookupUrl || null,
  }
}
